import { z } from 'zod';
import { jsonObjectSchema, jsonPrimitiveSchema } from './json.ts';

export const observationKindSchema = z.enum([
  'scalar',
  'series',
  'event-series',
  'distribution',
  'table',
  'graph',
  'spatial',
]);

export const datasetUpdateModeSchema = z.enum(['replace', 'append', 'patch']);

export const dimensionDefinitionSchema = z.object({
  key: z.string().min(1),
  label: z.string().default(''),
  type: z.enum([
    'entity',
    'time',
    'number',
    'category',
    'boolean',
    'text',
  ]),
  unit: z.string().optional(),
  nullable: z.boolean().default(false),
  semanticRole: z.string().optional(),
}).strict();

export const observationRowSchema = z.object({
  id: z.string().min(1),
  values: z.record(z.string(), jsonPrimitiveSchema),
}).strict();

export const datasetRetentionSchema = z.object({
  maxRows: z.number().int().positive().max(100000).optional(),
  timeWindow: z.number().positive().finite().optional(),
  timeField: z.string().min(1).optional(),
}).strict().superRefine((retention, context) => {
  if (retention.timeWindow !== undefined && !retention.timeField) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['timeField'],
      message: '按时间裁剪数据时必须指定 timeField',
    });
  }
});

export const observationDatasetSchema = z.object({
  id: z.string().min(1),
  schemaVersion: z.string().min(1),
  kind: observationKindSchema,
  revision: z.number().int().nonnegative(),
  updateMode: datasetUpdateModeSchema.default('replace'),
  primaryKey: z.string().optional(),
  dimensions: z.array(dimensionDefinitionSchema).max(1000).default([]),
  rows: z.array(observationRowSchema).max(100000).default([]),
  removedRowIds: z.array(z.string().min(1)).max(100000).optional(),
  retention: datasetRetentionSchema.optional(),
  metadata: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    entityKey: z.string().optional(),
    timeKey: z.string().optional(),
    producerId: z.string().optional(),
    runId: z.string().optional(),
    source: z.enum([
      'static',
      'runtime',
      'remote',
      'generated',
    ]).optional(),
    generatedAt: z.string().datetime().optional(),
    provenance: jsonObjectSchema.default({}),
    summary: z.record(z.string(), jsonPrimitiveSchema).optional(),
  }).strict().default({}),
}).strict().superRefine((dataset, context) => {
  const dimensionKeys = new Set<string>();
  for (const [index, dimension] of dataset.dimensions.entries()) {
    if (dimensionKeys.has(dimension.key)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dimensions', index, 'key'],
        message: `观察数据维度重复：${dimension.key}`,
      });
    }
    dimensionKeys.add(dimension.key);
  }

  const rowIds = new Set<string>();
  for (const [index, row] of dataset.rows.entries()) {
    if (rowIds.has(row.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rows', index, 'id'],
        message: `观察数据行编号重复：${row.id}`,
      });
    }
    rowIds.add(row.id);
  }

  const removedRowIds = new Set<string>();
  for (const [index, rowId] of (dataset.removedRowIds ?? []).entries()) {
    if (removedRowIds.has(rowId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['removedRowIds', index],
        message: `待删除的观察数据行编号重复：${rowId}`,
      });
    }
    if (rowIds.has(rowId)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['removedRowIds', index],
        message: `同一更新不能同时修改和删除观察数据行：${rowId}`,
      });
    }
    removedRowIds.add(rowId);
  }
  if (
    dataset.updateMode !== 'patch'
    && (dataset.removedRowIds?.length ?? 0) > 0
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['removedRowIds'],
      message: '只有 patch 更新可以删除观察数据行',
    });
  }

  if (dataset.primaryKey && !dimensionKeys.has(dataset.primaryKey)) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['primaryKey'],
      message: `主键字段未在维度中声明：${dataset.primaryKey}`,
    });
  }
});

export const datasetDeclarationSchema = z.object({
  id: z.string().min(1),
  kind: observationKindSchema.optional(),
  title: z.string().default(''),
  description: z.string().optional(),
  requiredFields: z.array(z.string().min(1)).max(1000).default([]),
}).strict().superRefine((declaration, context) => {
  const seen = new Set<string>();
  for (const [index, field] of declaration.requiredFields.entries()) {
    if (seen.has(field)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['requiredFields', index],
        message: `数据声明字段重复：${field}`,
      });
    }
    seen.add(field);
  }
});

export type ObservationKind = z.infer<typeof observationKindSchema>;
export type DatasetUpdateMode = z.infer<typeof datasetUpdateModeSchema>;
export type DimensionDefinition = z.infer<typeof dimensionDefinitionSchema>;
export type ObservationRow = z.infer<typeof observationRowSchema>;
export type DatasetRetention = z.infer<typeof datasetRetentionSchema>;
export type ObservationDataset = z.infer<typeof observationDatasetSchema>;
export type DatasetDeclaration = z.infer<typeof datasetDeclarationSchema>;
